import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Divider, Grid, Icon, Input, Table } from 'semantic-ui-react'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import { DownloadService } from '~/api/services/downloadFiles'
import MilkMoneyAccountingService, { TourDetailData } from '~/api/services/milkMoneyAccounting'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useDebounce } from 'use-lodash-debounce';
import { StyledGridColumns, StyledGridRow, StyledSegment } from '~/pages/Dairy/MilkFixedPrice/ShowMilkFixedPrice/style'
import { useAuth } from '~/routes/components/AuthProvider'
import { formatNumber, formatNumberWithoutFractions, handleErrors } from '~/utils/helpers'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { CellWrapper, ColumnLabels, StyledHeader } from './style'
import { El } from '~/components/Layout/style'
import { RouteTypes } from '~/routes/constants'
import AddEditMilkDelivery from './AddEditMilkDelivery'
import Dialog from '~/components/Dialog'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import ExcelExport from '~/components/ExcelExport'
import { Nullable } from '~/types'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Tour data id'),
    key: 'uniqueId',
    sorted: 'desc'
  },
  {
    name: i18n.t('Time from'),
    key: 'timeFrom',
    sorted: 'desc'
  },
  {
    name: i18n.t('Time to'),
    key: 'timeTo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    type: 'supplier-link',
    sorted: 'desc'
  },
  {
    name: i18n.t('Farmer'),
    key: 'companyName',
    type: 'supplier-link',
    sorted: 'desc'
  },
  {
    name: i18n.t('Amount accepted (in l)'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('avg. Temp. (°C)'),
    key: 'avgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  },
  {
    name: i18n.t('Sample bottle'),
    key: 'sampleBottleNumber'
  },
  {
    name: i18n.t('Sample Compartment Temp (°C)'),
    key: 'sampleAvgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const ShowTours: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [toursData, setToursData] = useState<Nullable<TourDetailData>>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [search, setSearch] = useState('')
  const [changeLogTableData, setChangeLogTableData] = useState(null)
  const [changeLogsLoading, setChangeLogsLoading] = useState(false)
  const debouncedSearchValue = useDebounce(search, 300)
  const [idMilkDelivery, setIdMilkDelivery] = useState(null)
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const [deleteTourLoading, setDeleteTourLoading] = useState(false)
  const [deleteTourId, setDeleteTourId] = useState(null)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: '',
    column: '',
    sort: ''
  })

  const actions = [
    {
      popupMessage: i18n.t('Edit'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit'
    }
  ]

  const changesLabelValues = {
    supplierNo: t('Supplier Number'),
    amount: t('Delivered amount (in l)'),
    avgTemp: t('avg. Temp. (°C)'),
    uniqueId: t('Unique id'),
    timeFrom: t('Time from'),
    timeTo: t('Time to')
  }
  
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())

  const {
    isLoading: isBusyData,
    data: specificTourData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getMilkMoneySpecificTourData(id, {
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await MilkMoneyAccountingService.getMoneyToursData(id)
      setToursData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleDownloadFile(name) {
    try {
      await DownloadService.downloadFile(`milk-money/tours/${id}/get-file`, name)
    } catch (error) {
      handleErrors(error)
    }
  }

  function renderOriginalFiles() {
    return <div>
      {toursData?.file1 && <div
        style={{ color: '#007bff', cursor: 'pointer', overflowWrap: 'anywhere' }}
        onClick={() => handleDownloadFile(toursData?.file1)}
      >
        {toursData?.file1}
      </div>}
      {toursData?.file2 && <div
        style={{ color: '#007bff', cursor: 'pointer', overflowWrap: 'anywhere' }}
        onClick={() => handleDownloadFile(toursData?.file2)}
      >
        {toursData?.file2}
      </div>}
    </div>
  }

  function importedManuallyLabel() {
    const date = dayjs(toursData?.createdAt).format(('DD.MM.YYYY HH:mm'))
    return <>
      {toursData?.ftpImport === 0 ? date : <Icon
        size='large'
        color={'red'}
        name={'close'}
      />}
    </>
  }

  function renderShowTours() {
    return <StyledSegment placeholder>
      <Grid columns={2} stackable>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Column>
          <Grid columns={2} stackable>
            <Grid.Row>
              <ColumnLabels>{t('Accepted quantity (in l)')}</ColumnLabels>
              <StyledGridColumns>{formatNumberWithoutFractions(toursData?.totalAmount)}</StyledGridColumns>
            </Grid.Row>
            <Grid.Row>
              <ColumnLabels>{t('Collection truck no.')}</ColumnLabels>
              <StyledGridColumns>{toursData?.vehicleNumber}</StyledGridColumns>
            </Grid.Row>
            <Grid.Row>
              <ColumnLabels>{t('Driver no.')}</ColumnLabels>
              <StyledGridColumns>{toursData?.driverNumber}</StyledGridColumns>
            </Grid.Row>
            <Grid.Row>
              <ColumnLabels>{t('Number of samples')}</ColumnLabels>
              <StyledGridColumns>{toursData?.samplesTotal}</StyledGridColumns>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column>
          <Grid columns={2} stackable style={{ marginLeft: 5 }}>
            <StyledGridRow>
              <ColumnLabels>{t('Tour star / Tour end')}</ColumnLabels>
              <StyledGridColumns>
                {`${dayjs(toursData?.dateStart).format(('DD.MM.YYYY HH:mm'))} / ${dayjs(toursData?.dateEnd).format(('DD.MM.YYYY HH:mm'))}`}
              </StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <ColumnLabels>{t('Acquisition system')}</ColumnLabels>
              <StyledGridColumns>{toursData?.typeLabel ? toursData.typeLabel : t('Not defined')}</StyledGridColumns>
            </StyledGridRow>
            <Grid.Row>
              <ColumnLabels>{t('Original files')}</ColumnLabels>
              <Grid.Column style={{ paddingLeft: 0 }}>{renderOriginalFiles()}</Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ alignItems: 'end' }}>
              <ColumnLabels>{t('Imported automatically?')}</ColumnLabels>
              <StyledGridColumns>
                {toursData?.ftpImport ? dayjs(toursData?.createdAt).format(('DD.MM.YYYY HH:mm')) : <Icon
                  size='large'
                  color={'red'}
                  name={'close'}
                />}
              </StyledGridColumns>
            </Grid.Row>
            <StyledGridRow>
              <ColumnLabels>{t('Imported manually?')}</ColumnLabels>
              <StyledGridColumns>{importedManuallyLabel()}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
      </Grid>
    </StyledSegment>
  }

  function renderSearch() {
    return (
      <Input
        style={{ width: 215 }}
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Farmer / Supplier number')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  function renderHeader() {
    return <StyledHeader>
      <div className='header'>{t('Milk collection from farmers')}</div>
      {renderSearch()}
    </StyledHeader>
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'edit':
        return setIdMilkDelivery(row.id)
      case 'delete':
        return setDeleteTourId(row.id)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      actions={lightUserLogged || !toursData?.canBeEdited ? [] : actions}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderSpecificTourData() {
    return <div>
      {renderHeader()}
      {renderTable()}
    </div>
  }

  async function handleShowChangeLogsData() {
    setChangeLogsLoading(true)
    try {
      const response = await MilkMoneyAccountingService.getChangesToursData(id, { ...dataFiltered })
      setChangeLogTableData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setChangeLogsLoading(false)
  }

  function renderActions() {
    return <>
      {!lightUserLogged && Boolean(toursData?.canBeEdited) && <El.PageHeaderActionButton
        size='large'
        onClick={() => setIdMilkDelivery('create')}
      >
        {t('Add milk delivery')}
      </El.PageHeaderActionButton>
      }
      <El.PageHeaderActionButton
        size='large'
        onClick={() => handleShowChangeLogsData()}
        loading={changeLogsLoading}
      >
        {t('Change logs')}
      </El.PageHeaderActionButton>
      {!lightUserLogged && <ExcelExport
        name={t('Excel export')}
        fileName={'exel_file'}
        url={`milk-money/tours/${id}/export`}
      />}
    </>
  }

  function getValueChangeLogCell(value, key) {
    switch (key) {
      case 'date':
        return dayjs(value).format('DD.MM.YYYY HH:mm')
      case 'avgTemp':
        return formatNumber(value, 1)
      case 'amount':
        return formatNumber(value, 1)
      default:
        return value
    }
  }

  function renderCellChangesLog(changes, type) {
    return <CellWrapper>
      {Object.keys(changes[0]).map(changeKey => {
        if (Object.keys(changesLabelValues).find(key => key === changeKey)) {
          const value = changeKey === 'uniqueId' ? changes[0][changeKey] : getValueChangeLogCell(changes[0][changeKey][type], changeKey)
          const label = changesLabelValues[changeKey]
          return <div key={value}>{`${label}: ${value}`}</div>
        }
      })}
    </CellWrapper>
  }

  function renderChangeLogTable() {
    return <Table celled fixed singleLine>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('Type')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Old values')}</Table.HeaderCell>
          <Table.HeaderCell>{t('New values')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      {changeLogTableData?.rows?.map(row => {
        return <Table.Row key={row?.id}>
          <Table.Cell><CellWrapper>{row.typeLabel}</CellWrapper></Table.Cell>
          <Table.Cell>{row.type === 'edit' || row.type === 'delete' ? renderCellChangesLog(row?.changes, 'old') : '-'}</Table.Cell>
          <Table.Cell>{row.type !== 'delete' && renderCellChangesLog(row?.changes, 'new')}</Table.Cell>
        </Table.Row>
      })}
    </Table>
  }

  function renderShowChangesLog() {
    return <Dialog
      isDialogOpen={Boolean(changeLogTableData)}
      handleClose={() => setChangeLogTableData(null)}
      textHeader={t('Change logs')}
      textClose={t('Close')}
      textContent={<>
        {renderChangeLogTable()}
      </>}
    />
  }

  async function handleDeleteTour() {
    setDeleteTourLoading(true)
    try {
      await MilkMoneyAccountingService.deleteMilkDeliveryData(id, deleteTourId)
      setDeleteTourId(null)
      notify(t('Successfully deleted tour'), 'success')
      fetchFilteredData()
    } catch (error) {
      handleErrors(error)
    }
    setDeleteTourLoading(false)
  }

  function renderDeleteTourModal() {
    const farmerMilkRow = rows?.find(farmerMilkRow => farmerMilkRow.id === deleteTourId)
    return <Dialog
      isDialogOpen={Boolean(deleteTourId)}
      handleClose={() => setDeleteTourId(null)}
      textClose={t('Cancel')}
      textSubmit={t('Delete')}
      disableSubmit={deleteTourLoading}
      isLoading={deleteTourLoading}
      style={{ minWidth: 'fit-content' }}
      textHeader={t('Delete milk collection from farmer?')}
      textContent={t(
        'Do you really want to delete the milk collection from farmer  {{farmerName}} ({{farmerSupNr}})?',
        { farmerName: farmerMilkRow?.companyName, farmerSupNr: farmerMilkRow?.supplierNo }
      )}
      handleSubmit={() => handleDeleteTour()}
      type='abort'
    />
  }

  const { filtered, rows } = specificTourData ? specificTourData : { filtered: 0, rows: [] };

  return <Layout
    isBusy={isBusy || isBusyData}
    title={toursData && t('Details Tour-Nr. {{number}} from {{date}}',
      {
        number: toursData?.tourNumber,
        date: dayjs(toursData?.createdAt).format('DD.MM.YYYY')
      }
    )}
    segmented
    actions={renderActions()}
    close={RouteTypes.MILK_MONEY_DELIVERY}
  >
    {Boolean(deleteTourId) && renderDeleteTourModal()}
    {Boolean(changeLogTableData) && renderShowChangesLog()}
    {toursData && renderShowTours()}
    {renderSpecificTourData()}
    <AddEditMilkDelivery
      idMilkDelivery={idMilkDelivery}
      isVisible={Boolean(idMilkDelivery)}
      handleHiddenDrawer={(shouldUpdate?) => {
        setIdMilkDelivery(null)
        if (shouldUpdate) {
          fetchFilteredData()
        }
      }}
    />
  </Layout>
}
export default ShowTours